import React, { Component, ReactNode } from "react";
import styles from "../../pages/onboarding/OnboardingFlow.module.css";
import containerStyles from "./OnboardingContainer.module.css";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Error boundary component specifically designed for the onboarding flow.
 * Provides graceful error handling with user-friendly messages and recovery options.
 */
export default class OnboardingErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      "Onboarding error boundary caught an error:",
      error,
      errorInfo
    );
    this.setState({
      error,
      errorInfo,
    });

    // You could also log to an error reporting service here
    // errorReportingService.logError(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleRestart = () => {
    // Clear any stored onboarding data and restart
    localStorage.removeItem("onboarding_data");
    window.location.href = "/onboarding";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={containerStyles.onboardingWrapper}>
          <main className={styles.main}>
            <div className={containerStyles.container}>
              <div className={containerStyles.errorFallback}>
                <div className={containerStyles.errorIcon}>⚠️</div>
                <h2 className={containerStyles.errorTitle}>
                  Oops! Something went wrong
                </h2>
                <p className={containerStyles.errorMessage}>
                  We encountered an unexpected error during your onboarding
                  process. Don&apos;t worry - your progress has been saved and
                  you can continue where you left off.
                </p>

                <div className={containerStyles.errorActions}>
                  <button
                    onClick={this.handleRetry}
                    className={containerStyles.primaryButton}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={this.handleRestart}
                    className={containerStyles.secondaryButton}
                  >
                    Restart Onboarding
                  </button>
                  <button
                    onClick={() => (window.location.href = "/dashboard")}
                    className={containerStyles.linkButton}
                  >
                    Go to Dashboard
                  </button>
                </div>

                {process.env.NODE_ENV === "development" && this.state.error && (
                  <details className={containerStyles.errorDetails}>
                    <summary>Error Details (Development Only)</summary>
                    <div className={containerStyles.errorStack}>
                      <h4>Error Message:</h4>
                      <pre>{this.state.error.message}</pre>

                      <h4>Stack Trace:</h4>
                      <pre>{this.state.error.stack}</pre>

                      {this.state.errorInfo && (
                        <>
                          <h4>Component Stack:</h4>
                          <pre>{this.state.errorInfo.componentStack}</pre>
                        </>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}
